import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  DollarSign, 
  Plus, 
  Trash2, 
  Calculator, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default pricing configurations by model
const DEFAULT_PRICING_CONFIGS = {
  per_sqft: {
    model: 'per_sqft',
    default_markup_percent: 300,
    fixed_addition: 0,
    base_price_per_sqft: 2.50,
    cost_per_sqft: 0.625,
    min_sqft: 4,
    min_price: 25.00,
    volume_tiers: []
  },
  quantity_tiers: {
    model: 'quantity_tiers',
    default_markup_percent: 150,
    fixed_addition: 5.00,
    quantity_tiers: [
      { qty: 100, cost: 5.00, retail: null },
      { qty: 250, cost: 10.00, retail: null },
      { qty: 500, cost: 15.00, retail: null },
      { qty: 1000, cost: 25.00, retail: null }
    ]
  },
  base_plus_options: {
    model: 'base_plus_options',
    default_markup_percent: 300,
    fixed_addition: 25.00,
    size_prices: [
      { id: 'sm', name: 'Small', dimensions: '6 inches', cost: 15.00, retail: null },
      { id: 'md', name: 'Medium', dimensions: '8 inches', cost: 22.00, retail: null },
      { id: 'lg', name: 'Large', dimensions: '10 inches', cost: 35.00, retail: null }
    ],
    quantity_discounts: [
      { min_qty: 5, discount_percent: 5 },
      { min_qty: 10, discount_percent: 10 }
    ],
    base_lines_included: 3,
    extra_line_cost: 5.00,
    logo_cost: 25.00
  }
};

export default function TemplateEditor({ templateId, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [optionSets, setOptionSets] = useState([]);
  const [pricingPreview, setPricingPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    code: '',
    product_type: 'LGF',
    status: 'active',
    description: '',
    pricing: DEFAULT_PRICING_CONFIGS.per_sqft,
    option_set_ids: [],
    size_config: null,
    file_setup: null,
    default_specs: []
  });

  // Fetch existing template if editing
  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
    fetchOptionSets();
  }, [templateId]);

  // Fetch pricing preview when pricing config changes
  useEffect(() => {
    if (form.pricing) {
      fetchPricingPreview();
    }
  }, [form.pricing]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/${templateId}`);
      const data = await res.json();
      setForm({
        name: data.name || '',
        code: data.code || '',
        product_type: data.product_type || 'LGF',
        status: data.status || 'active',
        description: data.description || '',
        pricing: data.pricing || DEFAULT_PRICING_CONFIGS.per_sqft,
        option_set_ids: data.option_set_ids || [],
        size_config: data.size_config,
        file_setup: data.file_setup,
        default_specs: data.default_specs || []
      });
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptionSets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/option-sets`);
      const data = await res.json();
      setOptionSets(data.option_sets || []);
    } catch (error) {
      console.error('Error fetching option sets:', error);
    }
  };

  const fetchPricingPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      // Build sample config based on pricing model
      let sampleConfig = {};
      if (form.pricing.model === 'per_sqft') {
        sampleConfig = { width: 48, height: 96, quantity: 1 };
      } else if (form.pricing.model === 'quantity_tiers') {
        sampleConfig = { quantity: form.pricing.quantity_tiers?.[0]?.qty || 100 };
      } else if (form.pricing.model === 'base_plus_options') {
        sampleConfig = { size: form.pricing.size_prices?.[0]?.id || 'sm', quantity: 1 };
      }

      const res = await fetch(`${API_URL}/api/pricing/v2/preview/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pricing_config: form.pricing,
          sample_config: sampleConfig
        })
      });
      const data = await res.json();
      setPricingPreview(data);
    } catch (error) {
      console.error('Error fetching pricing preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [form.pricing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = templateId ? 'PUT' : 'POST';
      const url = templateId 
        ? `${API_URL}/api/templates/${templateId}`
        : `${API_URL}/api/templates`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        onSave();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePricingModelChange = (model) => {
    setForm(prev => ({
      ...prev,
      pricing: { ...DEFAULT_PRICING_CONFIGS[model] }
    }));
  };

  const updatePricing = (field, value) => {
    setForm(prev => ({
      ...prev,
      pricing: { ...prev.pricing, [field]: value }
    }));
  };

  // Calculate retail from cost
  const calculateRetail = (cost) => {
    const markup = form.pricing.default_markup_percent || 300;
    const fixed = form.pricing.fixed_addition || 0;
    return ((cost * (1 + markup / 100)) + fixed).toFixed(2);
  };

  // Render pricing preview panel
  const renderPricingPreview = () => (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            Live Pricing Preview
          </CardTitle>
          {previewLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
        </div>
      </CardHeader>
      <CardContent>
        {pricingPreview ? (
          <div className="space-y-4">
            {/* Markup Summary */}
            <div className="bg-white rounded-lg p-3 border">
              <p className="text-sm font-medium text-gray-700 mb-2">Markup Formula</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Cost</span>
                <span>×</span>
                <Badge variant="outline">{form.pricing.default_markup_percent}%</Badge>
                {form.pricing.fixed_addition > 0 && (
                  <>
                    <span>+</span>
                    <Badge variant="outline">${form.pricing.fixed_addition}</Badge>
                  </>
                )}
                <span>=</span>
                <span className="font-bold text-green-600">Retail</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Example: $10 cost → ${calculateRetail(10)} retail
              </p>
            </div>

            {/* Scenarios */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Price Scenarios</p>
              <div className="space-y-2">
                {pricingPreview.scenarios?.map((scenario, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 border text-sm">
                    <span className="text-gray-600">{scenario.name}</span>
                    <div className="flex items-center gap-3">
                      {scenario.cost !== undefined && (
                        <span className="text-gray-400">Cost: ${scenario.cost}</span>
                      )}
                      {scenario.sqft !== undefined && (
                        <span className="text-gray-400">{scenario.sqft} sq ft</span>
                      )}
                      <span className="font-bold text-green-600">${scenario.retail || scenario.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            <Info className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>Configure pricing to see preview</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render quantity tiers editor
  const renderQuantityTiersEditor = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Quantity Tiers</Label>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            const tiers = [...(form.pricing.quantity_tiers || [])];
            const lastQty = tiers.length > 0 ? tiers[tiers.length - 1].qty * 2 : 100;
            tiers.push({ qty: lastQty, cost: 0, retail: null });
            updatePricing('quantity_tiers', tiers);
          }}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Tier
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Quantity</th>
              <th className="px-3 py-2 text-left">Your Cost</th>
              <th className="px-3 py-2 text-left">Retail Price</th>
              <th className="px-3 py-2 text-left">Unit Price</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {form.pricing.quantity_tiers?.map((tier, idx) => {
              const retail = calculateRetail(tier.cost);
              const unitPrice = (retail / tier.qty).toFixed(4);
              return (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2">
                    <Input 
                      type="number"
                      value={tier.qty}
                      onChange={(e) => {
                        const tiers = [...form.pricing.quantity_tiers];
                        tiers[idx].qty = parseInt(e.target.value) || 0;
                        updatePricing('quantity_tiers', tiers);
                      }}
                      className="w-24 h-8"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-1">$</span>
                      <Input 
                        type="number"
                        step="0.01"
                        value={tier.cost}
                        onChange={(e) => {
                          const tiers = [...form.pricing.quantity_tiers];
                          tiers[idx].cost = parseFloat(e.target.value) || 0;
                          updatePricing('quantity_tiers', tiers);
                        }}
                        className="w-24 h-8"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-green-600">${retail}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-gray-500">${unitPrice}/ea</span>
                  </td>
                  <td className="px-3 py-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        const tiers = form.pricing.quantity_tiers.filter((_, i) => i !== idx);
                        updatePricing('quantity_tiers', tiers);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Render size prices editor
  const renderSizePricesEditor = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Size Options</Label>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            const sizes = [...(form.pricing.size_prices || [])];
            sizes.push({ id: `size_${sizes.length + 1}`, name: 'New Size', dimensions: '', cost: 0, retail: null });
            updatePricing('size_prices', sizes);
          }}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Size
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Dimensions</th>
              <th className="px-3 py-2 text-left">Cost</th>
              <th className="px-3 py-2 text-left">Retail</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {form.pricing.size_prices?.map((size, idx) => {
              const retail = calculateRetail(size.cost);
              return (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2">
                    <Input 
                      value={size.id}
                      onChange={(e) => {
                        const sizes = [...form.pricing.size_prices];
                        sizes[idx].id = e.target.value;
                        updatePricing('size_prices', sizes);
                      }}
                      className="w-20 h-8"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input 
                      value={size.name}
                      onChange={(e) => {
                        const sizes = [...form.pricing.size_prices];
                        sizes[idx].name = e.target.value;
                        updatePricing('size_prices', sizes);
                      }}
                      className="w-28 h-8"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input 
                      value={size.dimensions}
                      placeholder="e.g., 8 inches"
                      onChange={(e) => {
                        const sizes = [...form.pricing.size_prices];
                        sizes[idx].dimensions = e.target.value;
                        updatePricing('size_prices', sizes);
                      }}
                      className="w-28 h-8"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-1">$</span>
                      <Input 
                        type="number"
                        step="0.01"
                        value={size.cost}
                        onChange={(e) => {
                          const sizes = [...form.pricing.size_prices];
                          sizes[idx].cost = parseFloat(e.target.value) || 0;
                          updatePricing('size_prices', sizes);
                        }}
                        className="w-20 h-8"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-green-600">${retail}</span>
                  </td>
                  <td className="px-3 py-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        const sizes = form.pricing.size_prices.filter((_, i) => i !== idx);
                        updatePricing('size_prices', sizes);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Personalization Settings */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Personalization Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Lines Included</Label>
              <Input 
                type="number"
                value={form.pricing.base_lines_included || 1}
                onChange={(e) => updatePricing('base_lines_included', parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <Label className="text-xs">Extra Line Cost</Label>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">$</span>
                <Input 
                  type="number"
                  step="0.01"
                  value={form.pricing.extra_line_cost || 0}
                  onChange={(e) => updatePricing('extra_line_cost', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Logo Cost</Label>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">$</span>
                <Input 
                  type="number"
                  step="0.01"
                  value={form.pricing.logo_cost || 0}
                  onChange={(e) => updatePricing('logo_cost', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {templateId ? 'Edit Template' : 'Create New Template'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-3 gap-6">
            {/* Left side - Form */}
            <div className="col-span-2 space-y-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="pricing">Pricing</TabsTrigger>
                  <TabsTrigger value="options">Options</TabsTrigger>
                </TabsList>

                {/* Basic Info Tab */}
                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Template Name *</Label>
                      <Input 
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Vinyl Banners"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Code *</Label>
                      <Input 
                        value={form.code}
                        onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        placeholder="e.g., BAN"
                        maxLength={10}
                      />
                      <p className="text-xs text-gray-500">Used for SKU generation</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Product Type</Label>
                      <Select 
                        value={form.product_type} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, product_type: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LGF">Large Format</SelectItem>
                          <SelectItem value="SMF">Small Format</SelectItem>
                          <SelectItem value="AWD">Awards</SelectItem>
                          <SelectItem value="PROMO">Promotional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={form.status} 
                        onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this product type"
                      rows={3}
                    />
                  </div>
                </TabsContent>

                {/* Pricing Tab */}
                <TabsContent value="pricing" className="space-y-4 mt-4">
                  {/* Pricing Model Selection */}
                  <div className="space-y-2">
                    <Label>Pricing Model</Label>
                    <Select 
                      value={form.pricing.model} 
                      onValueChange={handlePricingModelChange}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_sqft">Per Square Foot (Large Format)</SelectItem>
                        <SelectItem value="quantity_tiers">Quantity Tiers (Small Format)</SelectItem>
                        <SelectItem value="base_plus_options">Base + Options (Awards)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Markup Settings */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Markup Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Default Markup %</Label>
                          <div className="flex items-center">
                            <Input 
                              type="number"
                              value={form.pricing.default_markup_percent}
                              onChange={(e) => updatePricing('default_markup_percent', parseFloat(e.target.value) || 0)}
                            />
                            <span className="ml-2 text-gray-500">%</span>
                          </div>
                          <p className="text-xs text-gray-500">300% = 4× multiplier</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Fixed Addition</Label>
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">$</span>
                            <Input 
                              type="number"
                              step="0.01"
                              value={form.pricing.fixed_addition}
                              onChange={(e) => updatePricing('fixed_addition', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <p className="text-xs text-gray-500">Added after markup calculation</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Model-specific Configuration */}
                  {form.pricing.model === 'per_sqft' && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Per Square Foot Settings</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Base Price per Sq Ft</Label>
                            <div className="flex items-center">
                              <span className="text-gray-500 mr-2">$</span>
                              <Input 
                                type="number"
                                step="0.01"
                                value={form.pricing.base_price_per_sqft}
                                onChange={(e) => updatePricing('base_price_per_sqft', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Cost per Sq Ft</Label>
                            <div className="flex items-center">
                              <span className="text-gray-500 mr-2">$</span>
                              <Input 
                                type="number"
                                step="0.01"
                                value={form.pricing.cost_per_sqft}
                                onChange={(e) => updatePricing('cost_per_sqft', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Minimum Sq Ft</Label>
                            <Input 
                              type="number"
                              value={form.pricing.min_sqft}
                              onChange={(e) => updatePricing('min_sqft', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Minimum Price</Label>
                            <div className="flex items-center">
                              <span className="text-gray-500 mr-2">$</span>
                              <Input 
                                type="number"
                                step="0.01"
                                value={form.pricing.min_price}
                                onChange={(e) => updatePricing('min_price', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {form.pricing.model === 'quantity_tiers' && renderQuantityTiersEditor()}

                  {form.pricing.model === 'base_plus_options' && renderSizePricesEditor()}
                </TabsContent>

                {/* Options Tab */}
                <TabsContent value="options" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Select Option Sets</Label>
                    <p className="text-sm text-gray-500">Choose which option sets apply to products using this template</p>
                  </div>

                  <div className="grid gap-3">
                    {optionSets.map(optionSet => {
                      const isSelected = form.option_set_ids.includes(optionSet.id);
                      return (
                        <div 
                          key={optionSet.id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                            isSelected ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              option_set_ids: isSelected
                                ? prev.option_set_ids.filter(id => id !== optionSet.id)
                                : [...prev.option_set_ids, optionSet.id]
                            }));
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">{optionSet.name}</h4>
                              <p className="text-sm text-gray-500">{optionSet.code} • {optionSet.choices?.length} choices</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {optionSet.applicable_types?.map(type => (
                                <Badge key={type} variant="outline" className="text-xs">{type}</Badge>
                              ))}
                              {isSelected && <CheckCircle className="h-5 w-5 text-blue-600" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Right side - Pricing Preview */}
            <div className="col-span-1">
              {renderPricingPreview()}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.name || !form.code}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {templateId ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
